import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { QuestionBankItem } from '../../../../../core/interfaces/question-search';
import { MathTextComponent } from '../../../../../shared/math/math-text.component';
import { MathPlainPipe } from '../../../../../shared/math/math-plain.pipe';

/**
 * Une ligne de la banque de questions.
 *
 * Purement présentationnel : reçoit un élément déjà chargé et n'appelle aucun
 * service. Le sélecteur est un attribut de `<tr>` afin que la table reste du
 * HTML valide (un composant `<app-…>` intercalé entre `<tbody>` et `<tr>`
 * casserait la mise en page tabulaire).
 */
@Component({
  selector: 'tr[app-question-row]',
  standalone: true,
  imports: [MathTextComponent, MathPlainPipe, DatePipe, TranslateModule],
  templateUrl: './question-row.component.html',
  styleUrl: './question-row.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'qr',
    '[class.qr--ai]': 'isAiGenerated()',
    '[class.qr--archived]': 'item().status === "archived"',
  },
})
export class QuestionRowComponent {
  readonly item = input.required<QuestionBankItem>();

  /** Demande d'édition — le parent décide comment (page, modale…). */
  /**
   * La ligne porte-t-elle une case a cocher ?
   *
   * <p>Une entree plutot qu'un affichage systematique : la meme ligne sert
   * ailleurs, la ou la selection multiple n'a pas de sens.</p>
   */
  readonly selectable = input(false);

  readonly selected = input(false);

  readonly selectedChange = output<boolean>();

  readonly edit = output<QuestionBankItem>();

  /** Demande de suppression — le parent confirme et appelle le service. */
  readonly remove = output<QuestionBankItem>();

  /**
   * Une question archivée n'est plus modifiable : elle a servi et reste figée
   * pour préserver les copies des élèves. On masque donc l'édition plutôt que
   * de laisser cliquer sur une action qui échouerait côté serveur.
   */
  readonly canEdit = computed(() => this.item().status !== 'archived');

  /** Idem pour la suppression : une question archivée ne se supprime pas. */
  readonly canDelete = computed(() => this.item().status !== 'archived');

  /**
   * Une question produite par une machine ne se relit pas comme une question
   * écrite à la main : l'origine est donc marquée sur la ligne entière, pas
   * seulement dans sa colonne.
   */
  readonly isAiGenerated = computed(() => this.item().origin === 'ai_generated');

  /**
   * Les valeurs du back sont en `snake_case` (`pending_review`) et les clés i18n
   * en `SCREAMING_SNAKE_CASE` : la conversion est faite ici plutôt que dans le
   * gabarit pour qu'une valeur inattendue produise une clé visible plutôt qu'un
   * libellé silencieusement faux.
   */
  readonly statusKey = computed(
    () => `QUESTION_BANK.STATUS.${this.item().status.toUpperCase()}`,
  );

  readonly originKey = computed(
    () => `QUESTION_BANK.ORIGIN.${this.item().origin.toUpperCase()}`,
  );

  readonly typeKey = computed(
    () => `QUESTION_BANK.TYPE.${(this.item().questionType ?? '').toUpperCase()}`,
  );

  readonly difficultyKey = computed(
    () =>
      `QUESTION_BANK.DIFFICULTY.${(this.item().difficultyLevel ?? '').toUpperCase()}`,
  );

  /** Titre du thème quand la recherche l'a hydraté, tiret cadratin sinon. */
  readonly themeLabel = computed(() => this.item().theme?.title ?? '—');
}
